import React from 'react';
import { UserCog, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

/**
 * @typedef {Object} UserEditFormState
 * @property {string} name
 * @property {string} email
 * @property {string} role
 * @property {boolean} is_active
 *
 * @typedef {Object} UserEditDialogProps
 * @property {boolean} open
 * @property {(open: boolean) => void} onOpenChange
 * @property {{id: string, email?: string, name?: string, role?: string, is_active?: boolean, is_cogniseg_global?: boolean} | null} selectedUser
 * @property {UserEditFormState} form
 * @property {(updater: (prev: UserEditFormState) => UserEditFormState) => void} setForm
 * @property {() => Promise<void>} onSave
 * @property {boolean} saving
 * @property {string} error
 * @property {string} success
 * @property {{value: string, label: string}[]} roles
 */

/**
 * User edit modal (name, email, role, is_active) for tenant users.
 *
 * Kept intentionally stateless: state and handlers live in the parent
 * (`TenantsManagementPage`) so the diff-based PATCH logic and error/success
 * banners stay in one place. See parent `handleEditUser` for the fix that
 * closed the "Guardar does nothing" bug (2026-09-08).
 *
 * @param {UserEditDialogProps} props
 */
export const UserEditDialog = ({
  open, onOpenChange, selectedUser, form, setForm,
  onSave, saving, error, success, roles,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-50 border-slate-200 text-slate-900" data-testid="edit-user-dialog">
      <DialogHeader>
        <DialogTitle className="text-slate-900 flex items-center gap-2">
          <UserCog className="w-5 h-5 text-indigo-700" /> Editar Utilizador
        </DialogTitle>
        <DialogDescription className="text-slate-500">{selectedUser?.email}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-slate-600">Nome</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            className="bg-white border-slate-300 text-slate-900"
            data-testid="edit-user-name-input"
          />
        </div>
        <div>
          <Label className="text-slate-600">Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
            className="bg-white border-slate-300 text-slate-900"
            data-testid="edit-user-email-input"
            disabled={selectedUser?.is_cogniseg_global}
          />
          {selectedUser?.email && form.email.trim().toLowerCase() !== selectedUser.email.toLowerCase() && (
            <p className="text-xs text-amber-700 mt-1">
              Vais alterar o email de login deste utilizador. Depois de guardar, ele terá de usar{' '}
              <strong>{form.email.trim().toLowerCase() || '(vazio)'}</strong> para entrar.
            </p>
          )}
          {selectedUser?.is_cogniseg_global && (
            <p className="text-xs text-slate-500 mt-1">
              Email gerido em Definições da Plataforma &gt; CogniSeg.
            </p>
          )}
        </div>
        <div>
          <Label className="text-slate-600">Perfil</Label>
          <Select value={form.role} onValueChange={(v) => setForm(prev => ({ ...prev, role: v }))}>
            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="edit-user-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              {roles.map(role => (
                <SelectItem key={role.value} value={role.value} className="text-slate-900 hover:bg-slate-100">
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
          <div>
            <Label className="text-slate-900">Conta ativa</Label>
            <p className="text-xs text-slate-500 mt-0.5">Desativar impede o utilizador de entrar</p>
          </div>
          <Switch
            checked={form.is_active}
            onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
            data-testid="edit-user-active-switch"
          />
        </div>
        {error && (
          <div className="p-3 rounded-lg border border-red-300 bg-red-50 text-sm text-red-800" data-testid="edit-user-error">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-sm text-emerald-800" data-testid="edit-user-success">
            {success}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
          Cancelar
        </Button>
        <Button
          onClick={onSave}
          disabled={saving || !form.name || !form.email}
          className="bg-indigo-700 hover:bg-amber-600 text-black"
          data-testid="save-user-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Guardar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
